import { useCallback, useEffect, useRef, useState } from 'react'
import { getErrorMessage } from '@features/shared/api/client'

/** A file on a form, from the moment it's picked until the form is sent. */
export interface PendingFile<TAttachment, TKind extends string = string> {
  localId: string
  fileName: string
  size: number
  kind: TKind
  status: 'uploading' | 'ready' | 'error'
  /** 0 to 1 while uploading. */
  progress: number
  error?: string
  /** This browser's own copy of an image, shown while it uploads. */
  previewUrl?: string
  /** The server's attachment, once uploaded. */
  attachment?: TAttachment
}

export interface UploadOptions {
  signal: AbortSignal
  onProgress: (fraction: number) => void
}

export interface FileAttachmentsConfig<TAttachment, TKind extends string> {
  /** Ready files transferred from another surface; used only on mount. */
  initialItems?: PendingFile<TAttachment, TKind>[]
  /** Whether files can be attached here now; turned off, the files go. */
  available: boolean
  /** The most a form carries; undefined until known, when nothing is taken. */
  maxFiles: number | undefined
  tooManyFilesMessage: string
  /** What a picked file looks like it is (an image gets a preview). */
  kindOf: (file: File) => TKind
  /** Why a file can't be attached, or null when it may be uploaded. */
  problemOf: (file: File) => string | null
  upload: (file: File, options: UploadOptions) => Promise<TAttachment>
  /** Deletes an upload taken off the form before it's sent. */
  remove: (attachment: TAttachment) => Promise<unknown>
  /** The name the server kept it under (a photo saved as .jpg). */
  uploadedName: (attachment: TAttachment) => string
  /**
   * Leaving the form discards its draft (the support panel closing), so the
   * files uploaded for it are deleted then, not a day later. Off by default:
   * a chat composer can leave and come back to the same files.
   */
  deleteUnsentOnUnmount?: boolean
}

/**
 * The files on a form (the chat composer, the support panel): each uploads
 * the moment it's picked, pasted or dropped, so sending the form stays a
 * small request that names them. Files uploaded but never sent are deleted
 * by the server after a day.
 */
export function useFileAttachments<TAttachment, TKind extends string>(
  config: FileAttachmentsConfig<TAttachment, TKind>
) {
  const { available } = config
  const configRef = useRef(config)
  configRef.current = config

  const [items, setItems] = useState<PendingFile<TAttachment, TKind>[]>(
    () => config.initialItems ?? []
  )
  const [notice, setNotice] = useState<string | null>(null)
  const itemsRef = useRef(items)
  itemsRef.current = items
  const uploads = useRef(new Map<string, AbortController>())

  const update = useCallback((localId: string, patch: Partial<PendingFile<TAttachment, TKind>>) => {
    setItems((prev) =>
      prev.map((item) => (item.localId === localId ? { ...item, ...patch } : item))
    )
  }, [])

  const forget = useCallback((item: PendingFile<TAttachment, TKind>) => {
    uploads.current.get(item.localId)?.abort()
    uploads.current.delete(item.localId)
    if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
  }, [])

  // Leaving the form stops its uploads.
  useEffect(() => {
    const running = uploads.current
    return () => {
      running.forEach((controller) => controller.abort())
      running.clear()
      itemsRef.current.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl))
      if (!configRef.current.deleteUnsentOnUnmount) return
      for (const item of itemsRef.current) {
        if (item.attachment) void configRef.current.remove(item.attachment).catch(() => {})
      }
    }
  }, [])

  // Turned off: the files on the form can't go.
  useEffect(() => {
    if (available) return
    itemsRef.current.forEach(forget)
    setItems([])
    setNotice(null)
  }, [available, forget])

  const addFiles = useCallback(
    (files: File[]) => {
      const { maxFiles, tooManyFilesMessage, kindOf, problemOf, upload, uploadedName } =
        configRef.current
      if (!configRef.current.available || maxFiles === undefined || files.length === 0) return
      const room = maxFiles - itemsRef.current.length
      setNotice(files.length > room ? tooManyFilesMessage : null)
      const added = files.slice(0, Math.max(room, 0)).map((file) => {
        const problem = problemOf(file)
        const kind = kindOf(file)
        const item: PendingFile<TAttachment, TKind> = {
          localId: crypto.randomUUID(),
          fileName: file.name || 'Pasted file',
          size: file.size,
          kind,
          status: problem ? 'error' : 'uploading',
          progress: 0,
          ...(problem ? { error: problem } : {}),
          ...(kind === 'image' && !problem && typeof URL.createObjectURL === 'function'
            ? { previewUrl: URL.createObjectURL(file) }
            : {}),
        }
        return { item, file, problem }
      })
      setItems((prev) => [...prev, ...added.map(({ item }) => item)])

      for (const { item, file, problem } of added) {
        if (problem) continue
        const controller = new AbortController()
        uploads.current.set(item.localId, controller)
        upload(file, {
          signal: controller.signal,
          onProgress: (progress) => update(item.localId, { progress }),
        })
          .then((attachment) => {
            if (controller.signal.aborted) return
            update(item.localId, {
              status: 'ready',
              progress: 1,
              attachment,
              fileName: uploadedName(attachment),
            })
          })
          .catch((error: unknown) => {
            if (controller.signal.aborted) return
            update(item.localId, { status: 'error', error: getErrorMessage(error) })
          })
          .finally(() => {
            if (uploads.current.get(item.localId) === controller)
              uploads.current.delete(item.localId)
          })
      }
    },
    [update]
  )

  const remove = useCallback(
    (localId: string) => {
      const item = itemsRef.current.find((candidate) => candidate.localId === localId)
      if (!item) return
      forget(item)
      setNotice(null)
      setItems((prev) => prev.filter((candidate) => candidate.localId !== localId))
      // Uploaded but not sent: the server deletes it now rather than in a day.
      if (item.attachment) void configRef.current.remove(item.attachment).catch(() => {})
    },
    [forget]
  )

  /** After a send: the files now belong to what was sent, so nothing is deleted. */
  const clear = useCallback(() => {
    itemsRef.current.forEach(forget)
    setNotice(null)
    setItems([])
  }, [forget])

  /** Hands an uploaded file to what keeps it (an email step's attachments): nothing is deleted. */
  const take = useCallback(
    (localId: string) => {
      const item = itemsRef.current.find((candidate) => candidate.localId === localId)
      if (!item) return
      forget(item)
      setItems((prev) => prev.filter((candidate) => candidate.localId !== localId))
    },
    [forget]
  )

  /** Takes every file off the form, deleting the uploaded ones (a discarded draft). */
  const removeAll = useCallback(() => {
    itemsRef.current.forEach((item) => remove(item.localId))
  }, [remove])

  const ready = items.flatMap((item) =>
    item.status === 'ready' && item.attachment ? [item.attachment] : []
  )
  return {
    items,
    /** A note about the whole set, such as too many files. */
    notice,
    ready,
    isUploading: items.some((item) => item.status === 'uploading'),
    hasErrors: items.some((item) => item.status === 'error'),
    addFiles,
    remove,
    removeAll,
    take,
    clear,
  }
}
